import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Upload, X, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { Reveal, Overline, CONTACT, track } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESTATIONS = [
  "Entretien d'immeuble / PPE",
  "Nettoyage",
  "Fin de bail",
  "Fin de chantier",
  "Nettoyage haute pression",
  "Garage / parking",
  "Travaux / réparations",
  "Débarras",
  "Entretien extérieur",
  "Autre",
];

const inputCls =
  "w-full border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-lagoon focus:bg-white/10";
const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-lagoon";

export const DevisForm = () => {
  const [form, setForm] = useState({
    nom: "", organisation: "", telephone: "", email: "", localite: "",
    prestation: PRESTATIONS[0], description: "", contact_prefere: "Téléphone",
  });
  const [photos, setPhotos] = useState([]);
  const [honey, setHoney] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((prev) => [...prev, ...files].slice(0, 8));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("website", honey);
      photos.forEach((p) => data.append("photos", p));
      await axios.post(`${API}/devis`, data);
      setSent(true);
      track("generate_lead", { prestation: form.prestation });
    } catch (err) {
      toast.error("L'envoi a échoué. Réessayez ou appelez-nous directement.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="devis" className="bg-nuit py-24 lg:py-36" data-testid="devis-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline light>Demande de devis</Overline>
              <h2 className="font-display mt-4 text-3xl font-medium leading-tight tracking-tight text-white sm:text-5xl">
                Un besoin ?<br />Parlons-en.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
                Décrivez votre demande en quelques lignes, ajoutez des photos si utile :
                vous recevrez une proposition claire, sans engagement. Devis gratuit.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 space-y-5">
                <a href={CONTACT.phoneHref} data-testid="devis-contact-phone" className="flex items-center gap-4 text-white/80 transition-colors duration-300 hover:text-lagoon">
                  <span className="flex h-11 w-11 items-center justify-center border border-lagoon/30"><Phone className="h-4 w-4 text-lagoon" /></span>
                  <span className="text-sm font-semibold tracking-wide">{CONTACT.phone}</span>
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="devis-contact-email" className="flex items-center gap-4 text-white/80 transition-colors duration-300 hover:text-lagoon">
                  <span className="flex h-11 w-11 items-center justify-center border border-lagoon/30"><Mail className="h-4 w-4 text-lagoon" /></span>
                  <span className="text-sm font-semibold tracking-wide">{CONTACT.email}</span>
                </a>
                <div className="flex items-center gap-4 text-white/80">
                  <span className="flex h-11 w-11 items-center justify-center border border-lagoon/30"><MapPin className="h-4 w-4 text-lagoon" /></span>
                  <span className="text-sm font-semibold tracking-wide">{CONTACT.address}</span>
                </div>
              </div>
              <p className="mt-10 text-xs uppercase tracking-[0.2em] text-white/35">
                Fiabilité • Polyvalence • Proximité
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <Reveal>
                <div className="flex h-full min-h-[420px] flex-col items-start justify-center border border-lagoon/30 bg-white/5 p-10" data-testid="form-success-message">
                  <CheckCircle2 className="h-12 w-12 text-lagoon" />
                  <h3 className="font-display mt-6 text-2xl font-medium text-white sm:text-3xl">Demande envoyée, merci !</h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
                    Votre demande a bien été transmise et une confirmation vous a été envoyée par e-mail.
                    Steve Rosset vous recontactera personnellement dans les meilleurs délais.
                  </p>
                  <button
                    onClick={() => { setSent(false); setPhotos([]); }}
                    data-testid="form-new-request-button"
                    className="mt-8 rounded-full border border-white/25 px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-lagoon hover:text-lagoon"
                  >
                    Faire une autre demande
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal delay={0.08}>
                <form onSubmit={submit} data-testid="devis-form" className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="devis-nom" className={labelCls}>Nom / Prénom *</label>
                    <input id="devis-nom" data-testid="devis-field-nom" required value={form.nom} onChange={set("nom")} className={inputCls} placeholder="Votre nom" />
                  </div>
                  <div>
                    <label htmlFor="devis-organisation" className={labelCls}>Entreprise / PPE / Régie</label>
                    <input id="devis-organisation" data-testid="devis-field-organisation" value={form.organisation} onChange={set("organisation")} className={inputCls} placeholder="Si applicable" />
                  </div>
                  <div>
                    <label htmlFor="devis-telephone" className={labelCls}>Téléphone *</label>
                    <input id="devis-telephone" data-testid="devis-field-telephone" required type="tel" value={form.telephone} onChange={set("telephone")} className={inputCls} placeholder="07x xxx xx xx" />
                  </div>
                  <div>
                    <label htmlFor="devis-email" className={labelCls}>E-mail *</label>
                    <input id="devis-email" data-testid="devis-field-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="vous@exemple.ch" />
                  </div>
                  <div>
                    <label htmlFor="devis-localite" className={labelCls}>Localité</label>
                    <input id="devis-localite" data-testid="devis-field-localite" value={form.localite} onChange={set("localite")} className={inputCls} placeholder="Sion, Conthey, Sierre…" />
                  </div>
                  <div>
                    <label htmlFor="devis-prestation" className={labelCls}>Type de prestation *</label>
                    <select id="devis-prestation" data-testid="devis-field-prestation" value={form.prestation} onChange={set("prestation")} className={`${inputCls} appearance-none`}>
                      {PRESTATIONS.map((p) => <option key={p} value={p} className="bg-nuit text-white">{p}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="devis-description" className={labelCls}>Description de la demande *</label>
                    <textarea id="devis-description" data-testid="devis-field-description" required rows={4} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} placeholder="Décrivez votre bien, la prestation souhaitée, la fréquence éventuelle…" />
                  </div>
                  <div className="sm:col-span-2">
                    <span className={labelCls}>Moyen de contact préféré</span>
                    <div className="flex gap-3">
                      {["Téléphone", "E-mail"].map((m) => (
                        <button
                          type="button"
                          key={m}
                          onClick={() => setForm({ ...form, contact_prefere: m })}
                          data-testid={`devis-contact-pref-${m.toLowerCase().replace("é", "e")}`}
                          className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] transition-all duration-300 ${
                            form.contact_prefere === m ? "bg-lagoon text-nuit" : "border border-white/20 text-white/60 hover:border-lagoon/50"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <span className={labelCls}>Photos (facultatif, max. 8)</span>
                    <input ref={fileRef} type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" data-testid="devis-field-photos" />
                    <input type="text" name="website" value={honey} onChange={(e) => setHoney(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0" />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="devis-upload-button"
                      className="flex w-full items-center justify-center gap-3 border border-dashed border-white/25 px-4 py-6 text-sm text-white/50 transition-colors duration-300 hover:border-lagoon hover:text-lagoon"
                    >
                      <Upload className="h-4 w-4" /> Ajouter des photos du bien ou des surfaces concernées
                    </button>
                    {photos.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid="devis-photos-list">
                        {photos.map((p, i) => (
                          <span key={`${p.name}-${i}`} className="flex items-center gap-2 bg-white/10 px-3 py-1.5 text-xs text-white/80">
                            {p.name}
                            <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} data-testid={`devis-photo-remove-${i}`} aria-label={`Retirer ${p.name}`}>
                              <X className="h-3 w-3 text-lagoon" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="devis-submit-button"
                      className="group inline-flex items-center gap-3 rounded-full bg-lagoon px-9 py-4 text-sm font-bold uppercase tracking-[0.12em] text-nuit transition-all duration-300 hover:bg-white disabled:opacity-60"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {sending ? "Envoi en cours…" : "Recevoir une proposition"}
                    </button>
                    <p className="mt-4 text-[11px] leading-relaxed text-white/35">
                      Vos données sont utilisées uniquement pour traiter votre demande. Voir la politique de confidentialité.
                    </p>
                  </div>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
